import { useEffect, useRef } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAdminMe } from "../hooks/useApi";
import AdminSidebar from "../components/AdminSidebar";
import AdminHeader from "../components/AdminHeader";
import AdminMobileHeader from "../components/AdminMobileHeader";
import AdminMobileFooterNav from "../components/AdminMobileFooterNav";
import AdminFloatingAssistant from "../components/AdminFloatingAssistant";
import "../admin-theme.css";

// Maps each admin route to the permission required to view it.
// Direct URL navigation must be blocked the same way the sidebar hides tabs.
const ROUTE_PERMISSIONS: Record<string, string> = {
  "/admin/dashboard": "stats.view",
  "/admin/models": "models.view",
  "/admin/users": "users.view",
  "/admin/plans": "subscriptions.view",
  "/admin/roles": "roles.manage",
  "/admin/logs": "logs.view",
  "/admin/tickets": "tickets.view",
  "/admin/expenses": "expenses.view",
  "/admin/system": "system.view",
  "/admin/settings": "settings.view",
  "/admin/messages": "messages.view",
  "/admin/notifications": "",
  "/admin/partners": "partners.view",
  "/admin/factors": "factors.view",
};

export default function AdminLayout() {
  const { data: admin, isLoading, error } = useAdminMe();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Reset scroll to top on tab/route change (same bug as user dashboard).
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    if (mainRef.current && mainRef.current.scrollTop !== 0) {
      mainRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [location.pathname]);

  // Show loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--admin-bg)" }}>
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-sm" style={{ color: "var(--admin-text-secondary)" }}>در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  // Not authenticated → admin login
  if (!admin || error) {
    return <Navigate to="/admin/login" replace />;
  }

  // Not admin and has no staff role → redirect to user dashboard
  if (!admin.is_admin && (!admin.roles || admin.roles.length === 0)) {
    return <Navigate to="/dashboard" replace />;
  }

  const permissions = admin.permissions || [];
  const isAdmin = Boolean(admin.is_admin);

  // Route-level permission guard: block direct URL access to pages the
  // user has no permission for (mirrors backend require_permission).
  const required = ROUTE_PERMISSIONS[location.pathname];
  if (required && !isAdmin && !permissions.includes(required)) {
    // Redirect to the first page the user is allowed to see.
    const firstAllowed = Object.entries(ROUTE_PERMISSIONS).find(
      ([, perm]) => isAdmin || permissions.includes(perm)
    );
    return <Navigate to={firstAllowed?.[0] ?? "/admin/tickets"} replace />;
  }

  return (
    <div className="flex h-screen" style={{ background: "var(--admin-bg)" }}>
      {/* Sidebar (desktop only) */}
      <div className="hidden lg:block shrink-0">
        <AdminSidebar permissions={permissions} isAdmin={isAdmin} />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Desktop header */}
        <div className="hidden lg:block">
          <AdminHeader />
        </div>
        {/* Mobile header */}
        <AdminMobileHeader permissions={permissions} isAdmin={isAdmin} />
        <main ref={mainRef} className="flex-1 overflow-y-auto p-4 lg:p-6 pb-28 lg:pb-6">
          <div className="max-w-[1440px] mx-auto w-full">
            <Outlet />
          </div>
        </main>
        {/* Mobile curved footer navigation */}
        <AdminMobileFooterNav permissions={permissions} isAdmin={isAdmin} />
      </div>

      {/* Floating AI Assistant */}
      <AdminFloatingAssistant />
    </div>
  );
}
